<template>
  <Transition name="fade">
    <div
      v-if="shouldShowLockScreen"
      class="fixed inset-0 z-critical flex items-center justify-center"
      style="background: linear-gradient(165deg, #17505f 0%, #0b2e37 100%);"
    >
      <div class="text-center px-8 max-w-sm">
        <!-- Ledger-lines brand mark (same silhouette as the app icon) -->
        <div class="mx-auto mb-6 w-20 h-20 bg-white/15 rounded-2xl flex items-center justify-center">
          <svg class="w-9 h-9 text-white" fill="currentColor" viewBox="0 0 1024 1024" aria-hidden="true">
            <rect x="112" y="256" width="800" height="96" rx="16" />
            <rect x="112" y="464" width="624" height="96" rx="16" />
            <rect x="112" y="672" width="800" height="96" rx="16" />
          </svg>
        </div>

        <!-- App Name -->
        <h1 class="wordmark text-2xl text-white mb-2">
          <span class="wordmark__shared">felles</span><span class="wordmark__pot">potten</span>
        </h1>
        <p class="text-white/70 mb-8">{{ $t('biometric.locked_message') }}</p>

        <!-- Unlock Button -->
        <button
          @click="handleUnlock"
          :disabled="isUnlocking"
          class="w-full py-4 px-6 bg-white/15 hover:bg-white/25 disabled:bg-white/10 text-white font-semibold rounded-xl border border-white/30 transition-all duration-200 flex items-center justify-center gap-3"
        >
          <FingerprintIcon v-if="!isUnlocking" class="w-6 h-6" />
          <svg v-else class="w-6 h-6 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{{ isUnlocking ? $t('biometric.unlocking') : $t('biometric.unlock_button', { type: biometryTypeName }) }}</span>
        </button>

        <!-- Error Message -->
        <Transition name="slide-fade">
          <p v-if="errorMessage" class="mt-4 text-red-200 text-sm" role="alert">
            {{ errorMessage }}
          </p>
        </Transition>

        <!-- Escape hatch: biometrics can fail repeatedly (wet fingers, Face ID
             in the dark) — never leave retry as the only way forward. -->
        <Transition name="slide-fade">
          <button
            v-if="hasFailedAttempt"
            type="button"
            :disabled="isSigningOut"
            class="mt-4 w-full py-3 px-6 text-white/80 hover:text-white text-sm underline underline-offset-4 bg-transparent border-0 cursor-pointer disabled:opacity-50"
            @click="signOutFallback"
          >
            {{ isSigningOut ? $t('common.loading') : $t('biometric.use_password_fallback') }}
          </button>
        </Transition>

        <!-- Retry hint -->
        <p class="mt-6 text-white/50 text-xs">
          {{ $t('biometric.tap_to_retry') }}
        </p>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { logout } from "../stores/authStore"
import {
  biometryTypeName,
  disableBiometric,
  isInitializing,
  shouldShowLockScreen,
  unlock,
} from "../stores/biometricStore"
import FingerprintIcon from "./icons/FingerprintIcon.vue"

const { t } = useI18n()

const isUnlocking = ref(false)
const errorMessage = ref("")
const hasTriggeredPrompt = ref(false)
const hasFailedAttempt = ref(false)
const isSigningOut = ref(false)

async function handleUnlock() {
  if (isUnlocking.value) return

  isUnlocking.value = true
  errorMessage.value = ""

  try {
    const result = await unlock()

    if (!result.success) {
      errorMessage.value = result.error || t("biometric.auth_failed")
      hasFailedAttempt.value = true
    }
  } catch {
    errorMessage.value = t("biometric.auth_failed")
    hasFailedAttempt.value = true
  } finally {
    isUnlocking.value = false
  }
}

// Fallback when the biometric keeps failing: sign out to the password/social
// login screen. Biometric unlock is re-enabled from Settings after signing
// back in — leaving it on would just re-lock the login screen.
async function signOutFallback() {
  if (isSigningOut.value) return
  isSigningOut.value = true
  errorMessage.value = ""
  try {
    await logout()
    disableBiometric()
  } catch {
    errorMessage.value = t("biometric.auth_failed")
  } finally {
    isSigningOut.value = false
  }
}

// Trigger the biometric prompt as soon as the lock screen is showing and
// initialization has completed (immediate: also covers being already ready at
// setup, e.g. on app resume). `hasTriggeredPrompt` prevents double prompts and
// is re-armed whenever the lock screen is dismissed.
watch(
  [isInitializing, shouldShowLockScreen],
  ([initializing, showLock]) => {
    if (!showLock) {
      hasTriggeredPrompt.value = false
      hasFailedAttempt.value = false
      errorMessage.value = ""
      return
    }
    if (!initializing && !hasTriggeredPrompt.value && !isUnlocking.value) {
      hasTriggeredPrompt.value = true
      handleUnlock()
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
